// ============================================================================
// SignupForm — the expanded trainer registration (POST /api/auth/signup).
// ----------------------------------------------------------------------------
// Captures name, username, email, phone (E.164 via the locked PhoneField),
// password + confirm, and a terms agreement. On submit it calls
// useAuth().signup() which creates the account, sets the shared vf_session
// cookie (the new trainer is logged straight in), and — when an email was given
// — mints + emails a Wave-6 verification link. We then surface a "verify your
// email" prompt (reusing the same copy posture as EmailVerifyBanner) before
// continuing into the studio. The phone is captured but NOT SMS-verified this
// wave.
//
// VALIDATION is client-side first (required name/username, >=10-char password,
// matching confirm, valid E.164 phone if given, terms ticked), then the server's
// 409 (username/handle taken) / 422 (weak password) errors are mapped back to
// inline messages. Built on the locked Pulse design system (Card, TextField,
// PhoneField, Button) + Framer Motion (reduced-motion aware); the design system
// itself is untouched.
// ============================================================================
import { useId, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Button, Card, PhoneField, TextField, isE164 } from "../../components";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api";
import type { SignupResponse } from "../../api";
import i18n from "../../i18n/config";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.06 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

// A deliberately permissive email shape check — the real verification is the
// emailed link, so this just rejects obvious typos before the round-trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      {open ? (
        <>
          <path
            d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        </>
      ) : (
        <>
          <path
            d="M2.5 12S6 5.5 12 5.5c2 0 3.7.7 5.1 1.6M21.5 12S18 18.5 12 18.5c-2 0-3.7-.7-5.1-1.6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M4 4l16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <span
      className="h-5 w-5 animate-spin rounded-full border-[2.5px] border-white/40 border-t-white"
      aria-hidden
    />
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M4 7l8 6 8-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SignupForm({ dest }: { dest: string }) {
  const { t } = useTranslation("signup");
  const { signup } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [terms, setTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  // Field-scoped errors so the most relevant message sits under each input.
  const [fieldErr, setFieldErr] = useState<{
    username?: string;
    email?: string;
    phone?: string;
    password?: string;
    confirm?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  // Non-null once the account exists — switches the card to the verify prompt.
  const [done, setDone] = useState<SignupResponse | null>(null);

  const errorId = useId();
  const termsId = useId();

  const phoneInvalid = useMemo(
    () => phone !== "" && !isE164(phone),
    [phone],
  );

  const clearErrors = () => {
    setError(null);
    setFieldErr({});
  };

  const validate = (): boolean => {
    const next: typeof fieldErr = {};
    // Banner messages collect the errors that have no inline field slot
    // (name + terms); per-field errors render directly under their input.
    let banner: string | null = null;
    let ok = true;

    if (!name.trim()) {
      banner = banner ?? t("errors.nameRequired");
      ok = false;
    }
    if (!username.trim()) {
      next.username = t("errors.usernameRequired");
      ok = false;
    }
    if (email.trim() && !EMAIL_RE.test(email.trim())) {
      next.email = t("errors.emailInvalid");
      ok = false;
    }
    if (phoneInvalid) {
      next.phone = t("errors.phoneInvalid");
      ok = false;
    }
    if (password.length < 10) {
      next.password = t("errors.passwordTooShort");
      ok = false;
    }
    if (confirm !== password) {
      next.confirm = t("errors.passwordMismatch");
      ok = false;
    }
    if (!terms) {
      banner = banner ?? t("errors.termsRequired");
      ok = false;
    }

    setFieldErr(next);
    setError(banner);
    return ok;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await signup({
        username: username.trim(),
        password,
        display_name: name.trim() || null,
        email: email.trim() || null,
        phone: phone || null,
        locale: i18n.language?.startsWith("zh") ? "zh" : "en",
      });
      // Email given → show the verify prompt; otherwise go straight in.
      if (res.email_verification_required) {
        setDone(res);
      } else {
        navigate(dest, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          const detail = typeof err.detail === "string" ? err.detail : "";
          if (detail.includes("handle")) {
            setError(t("errors.handleTaken"));
          } else {
            setFieldErr((f) => ({ ...f, username: t("errors.usernameTaken") }));
          }
        } else if (err.status === 422) {
          const detail = typeof err.detail === "string" ? err.detail : "";
          if (detail.toLowerCase().includes("password")) {
            setFieldErr((f) => ({ ...f, password: t("errors.passwordTooShort") }));
          } else {
            setError(t("errors.generic"));
          }
        } else {
          setError(t("errors.generic"));
        }
      } else {
        setError(t("errors.generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- POST-SIGNUP: email verification prompt (reuses Wave-6 verify flow) ----
  if (done) {
    return (
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="w-full max-w-md"
      >
        <Card className="space-y-5 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-lime-soft text-lime-600">
            <MailIcon />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body sm:text-3xl">
              {t("verify.title")}
            </h1>
            <p className="mt-2 text-body-soft">
              {done.email_sent
                ? t("verify.blurb", { email: email.trim() })
                : t("verify.dispatchedOff")}
            </p>
          </div>
          {done.email_sent && (
            <p className="rounded-md bg-surface-sunken px-4 py-3 text-sm text-body-muted">
              {t("verify.sentNote")}
            </p>
          )}
          <Button
            block
            size="lg"
            iconRight={<ArrowRight />}
            onClick={() => navigate(dest, { replace: true })}
          >
            {t("verify.continueCta")}
          </Button>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={reduce ? undefined : stagger}
      initial={reduce ? undefined : "hidden"}
      animate={reduce ? undefined : "show"}
      className="w-full max-w-md"
    >
      <motion.div variants={reduce ? undefined : rise}>
        <Card className="space-y-5">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body sm:text-3xl">
              {t("form.title")}
            </h1>
            <p className="mt-1.5 text-body-soft">{t("form.blurb")}</p>
          </div>

          {error && (
            <motion.div
              key={error}
              role="alert"
              id={errorId}
              initial={reduce ? false : { opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: "auto",
                x: reduce ? 0 : [0, -7, 6, -4, 0],
              }}
              transition={{
                opacity: { duration: 0.25 },
                height: { duration: 0.25 },
                x: { duration: 0.4, ease: EASE },
              }}
              className="overflow-hidden"
            >
              <div className="flex items-start gap-2.5 rounded-md bg-coral-soft px-4 py-3 ring-1 ring-coral/30">
                <svg
                  viewBox="0 0 24 24"
                  className="mt-0.5 h-5 w-5 shrink-0 text-volt-600"
                  fill="none"
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M12 7.5v5M12 16h.01"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="text-sm font-bold text-volt-600">{error}</p>
              </div>
            </motion.div>
          )}

          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <motion.div variants={reduce ? undefined : rise}>
              <TextField
                label={t("form.name")}
                name="name"
                autoComplete="name"
                placeholder={t("form.namePlaceholder")}
                hint={t("form.nameHint")}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearErrors();
                }}
                required
                autoFocus
                disabled={submitting}
              />
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <TextField
                label={t("form.username")}
                name="username"
                autoComplete="username"
                placeholder={t("form.usernamePlaceholder")}
                hint={fieldErr.username ? undefined : t("form.usernameHint")}
                error={fieldErr.username}
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setFieldErr((f) => ({ ...f, username: undefined }));
                  setError(null);
                }}
                required
                disabled={submitting}
              />
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <TextField
                label={t("form.email")}
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t("form.emailPlaceholder")}
                hint={fieldErr.email ? undefined : t("form.emailHint")}
                error={fieldErr.email}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFieldErr((f) => ({ ...f, email: undefined }));
                  setError(null);
                }}
                disabled={submitting}
              />
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <PhoneField
                label={t("form.phone")}
                countryLabel={t("form.phoneCountryLabel")}
                placeholder={t("form.phonePlaceholder")}
                hint={fieldErr.phone ? undefined : t("form.phoneHint")}
                error={fieldErr.phone ?? (phoneInvalid ? t("errors.phoneInvalid") : undefined)}
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  setFieldErr((f) => ({ ...f, phone: undefined }));
                  setError(null);
                }}
                autoComplete="tel-national"
                disabled={submitting}
              />
            </motion.div>

            {/* Password — the show/hide toggle is layered over the input. The
                field keeps NO default hint so the toggle aligns to the input's
                bottom exactly (mirrors the sign-in field); the placeholder + the
                error slot below carry the "10 characters" guidance. */}
            <motion.div variants={reduce ? undefined : rise} className="relative">
              <TextField
                label={t("form.password")}
                name="new-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("form.passwordPlaceholder")}
                error={fieldErr.password}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErr((f) => ({ ...f, password: undefined }));
                  setError(null);
                }}
                required
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                disabled={submitting}
                aria-pressed={showPassword}
                aria-label={showPassword ? t("form.hidePassword") : t("form.showPassword")}
                className="absolute top-[1.85rem] right-1 grid h-12 w-10 place-items-center rounded-md text-body-faint transition-colors hover:text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt disabled:opacity-50"
              >
                <EyeIcon open={showPassword} />
              </button>
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <TextField
                label={t("form.confirmPassword")}
                name="confirm-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("form.confirmPasswordPlaceholder")}
                error={fieldErr.confirm}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setFieldErr((f) => ({ ...f, confirm: undefined }));
                  setError(null);
                }}
                required
                disabled={submitting}
              />
            </motion.div>

            {/* Terms agreement. */}
            <motion.div
              variants={reduce ? undefined : rise}
              className="flex items-start gap-3"
            >
              <input
                id={termsId}
                type="checkbox"
                checked={terms}
                disabled={submitting}
                onChange={(e) => {
                  setTerms(e.target.checked);
                  setError(null);
                }}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-line-strong text-volt accent-volt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
              />
              <label htmlFor={termsId} className="text-sm text-body-soft">
                {t("form.terms")}
              </label>
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <Button
                type="submit"
                block
                size="lg"
                disabled={submitting}
                iconRight={submitting ? <Spinner /> : <ArrowRight />}
              >
                {submitting ? t("form.creating") : t("form.submit")}
              </Button>
            </motion.div>
          </form>

          <motion.p
            variants={reduce ? undefined : rise}
            className="text-center text-sm text-body-soft"
          >
            {t("form.haveAccount")}{" "}
            <Link
              to="/login"
              className="font-bold text-coral transition-colors hover:text-volt-600"
            >
              {t("form.signInLink")}
            </Link>
          </motion.p>

          <motion.p
            variants={reduce ? undefined : rise}
            className="flex items-center justify-center gap-1.5 text-xs font-medium text-body-faint"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
              <rect x="5" y="10.5" width="14" height="9" rx="2.5" stroke="currentColor" strokeWidth="2" />
              <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t("form.secureNote")}
          </motion.p>
        </Card>
      </motion.div>
    </motion.div>
  );
}
